import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, LoadingState, Modal, Select, TenantSelector } from '@/components/ui';
import { broadcastSchedulesApi } from '@/api/broadcastSchedules';
import { templatesApi, type Template } from '@/api/templates';
import { ManualTemplatePicker } from '@/components/features/manual-broadcasts/ManualTemplatePicker';
import { SegmentPicker } from '@/components/features/manual-broadcasts/SegmentPicker';
import { ResolvedTemplatePreview } from '@/components/features/templates/ResolvedTemplatePreview';
import { useToast } from '@/hooks/useToast';
import { useTenantScope } from '@/hooks/useTenantScope';
import { filterTemplatesByChannel } from '@/config/channels';
import { isWabaBackedTemplate } from '@/features/templates/templateTaxonomy';

const STEPS = ['Название и сегмент', 'Канал и шаблон', 'Расписание', 'Подтверждение'];

const BROADCAST_CHANNELS = [
  { value: '', label: 'Авто (patient preferred / tenant priority)' },
  { value: 'whatsapp_waba', label: 'WhatsApp WABA' },
  { value: 'whatsapp_regular', label: 'WhatsApp Regular' },
  { value: 'telegram_bot', label: 'Telegram Bot' },
  { value: 'telegram_client', label: 'Telegram Client' },
  { value: 'max', label: 'MAX' },
  { value: 'sms', label: 'SMS' },
];

const DATE_GUARD_OPTIONS = [
  { value: '', label: 'Без дополнительной date-проверки' },
  { value: 'same_day', label: 'Отправлять только если дата сегмента совпадает с сегодняшней' },
];

export function BroadcastScheduleCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { showError, showSuccess } = useToast();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [name, setName] = useState('');
  const [segmentType, setSegmentType] = useState<'regular' | 'dynamic' | 'lk'>('regular');
  const [segmentId, setSegmentId] = useState(0);
  const [segmentName, setSegmentName] = useState('');
  const [channelOverride, setChannelOverride] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [scheduleType, setScheduleType] = useState<'daily' | 'weekly' | 'cron'>('daily');
  const [sendTime, setSendTime] = useState('10:00');
  const [cronExpr, setCronExpr] = useState('');
  const [timezone, setTimezone] = useState('Europe/Moscow');
  const [dateGuardStrategy, setDateGuardStrategy] = useState<'' | 'same_day'>('');
  const [dateGuardFields, setDateGuardFields] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [staggerMinutes, setStaggerMinutes] = useState(0);
  const [ttlMinutes, setTTLMinutes] = useState(1440);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState<Awaited<ReturnType<typeof templatesApi.preview>> | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setTemplates([]);
      return;
    }
    templatesApi.list(tenantId).then((r) => setTemplates(r.templates ?? [])).catch(() => setTemplates([]));
  }, [tenantId]);

  useEffect(() => {
    if (!isEdit || !id) return;
    const currentTenantId = tenantId;
    if (!currentTenantId) return;
    let cancelled = false;
    const loadSchedule = async () => {
      try {
        setLoading(true);
        const schedule = await broadcastSchedulesApi.get(id, currentTenantId);
        if (cancelled) return;
        setName(schedule.name);
        setSegmentType(schedule.config.segment_type || 'regular');
        setSegmentId(schedule.config.segment_id || 0);
        setSegmentName(schedule.config.segment_name || '');
        setChannelOverride(schedule.config.channel_override || '');
        setTemplateId(schedule.config.template_id || '');
        setScheduleType(schedule.schedule_type);
        setSendTime(schedule.send_time || '10:00');
        setCronExpr(schedule.cron_expr || '');
        setTimezone(schedule.timezone || 'Europe/Moscow');
        setDateGuardStrategy(schedule.config.date_guard?.strategy || '');
        setDateGuardFields((schedule.config.date_guard?.source_fields || []).join(', '));
        setIsActive(schedule.is_active);
        setStaggerMinutes(schedule.config.stagger_minutes || 0);
        setTTLMinutes(schedule.config.ttl_minutes || 1440);
      } catch (e: any) {
        showError(e?.response?.data?.error || 'Не удалось загрузить расписание');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadSchedule();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, tenantId]);

  const filteredTemplates = useMemo(() => filterTemplatesByChannel(templates, channelOverride), [templates, channelOverride]);
  const selectedTemplate = templates.find((item) => item.id === templateId);
  const normalizedDateGuardFields = useMemo(
    () => dateGuardFields.split(',').map((item) => item.trim()).filter(Boolean),
    [dateGuardFields],
  );

  useEffect(() => {
    if (templateId && !filteredTemplates.find((template) => template.id === templateId)) {
      setTemplateId('');
    }
  }, [templateId, filteredTemplates]);

  const canNext = () => {
    if (step === 0) return Boolean(tenantId && name.trim() && segmentId > 0);
    if (step === 1) return Boolean(templateId);
    if (step === 2) return Boolean(timezone && (scheduleType === 'cron' ? cronExpr.trim() : sendTime.trim()));
    return true;
  };

  const handleOpenTemplatePreview = async () => {
    if (!tenantId || !selectedTemplate) return;
    try {
      setTemplatePreviewLoading(true);
      const preview = await templatesApi.preview(
        selectedTemplate.id,
        Object.fromEntries((selectedTemplate.variables || []).map((variable) => [variable, `[${variable}]`])),
        tenantId,
        channelOverride || selectedTemplate.messenger || undefined,
      );
      setTemplatePreviewData(preview);
      setTemplatePreviewOpen(true);
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось построить предпросмотр шаблона');
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!tenantId) {
      showError('Выберите клинику');
      return;
    }
    if (!name.trim() || segmentId <= 0 || !templateId) {
      showError('Заполните обязательные поля');
      return;
    }
    if (dateGuardStrategy && normalizedDateGuardFields.length === 0) {
      showError('Укажите хотя бы одно поле даты сегмента для date guard');
      return;
    }
    if (scheduleType === 'cron' && !cronExpr.trim()) {
      showError('Укажите cron выражение');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        name: name.trim(),
        campaign_type: 'segment_schedule',
        schedule_type: scheduleType,
        cron_expr: scheduleType === 'cron' ? cronExpr.trim() : undefined,
        timezone,
        send_time: sendTime,
        is_active: isActive,
        config: {
          template_id: templateId,
          template_name: selectedTemplate?.name,
          segment_type: segmentType,
          segment_id: segmentId,
          segment_name: segmentName || `Сегмент #${segmentId}`,
          channel_override: channelOverride || undefined,
          broadcast_group: 'segment_schedule',
          date_guard: dateGuardStrategy ? {
            strategy: dateGuardStrategy,
            source_fields: normalizedDateGuardFields,
            timezone,
          } : undefined,
          stagger_minutes: staggerMinutes || 0,
          ttl_minutes: ttlMinutes || 1440,
        },
      } as const;

      if (isEdit && id) {
        await broadcastSchedulesApi.update(id, payload, tenantId);
        showSuccess('Расписание обновлено');
      } else {
        await broadcastSchedulesApi.create(payload, tenantId);
        showSuccess('Расписание создано');
      }
      navigate('/broadcast-schedules');
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось сохранить расписание');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout
      headerTitle={isEdit ? 'Изменить регулярную рассылку' : 'Новая регулярная рассылка'}
      headerActions={(
        <Button variant="ghost" onClick={() => navigate('/broadcast-schedules')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Назад
        </Button>
      )}
    >
      <div className="p-6 max-w-2xl mx-auto">
        {isSuperAdmin && (
          <div className="mb-4">
            <TenantSelector
              value={selectedTenantId || ''}
              onChange={setSelectedTenant}
              required
              testId="broadcast-schedule-tenant-selector"
            />
          </div>
        )}

        <div className="flex gap-2 mb-6">
          {STEPS.map((label, index) => (
            <div
              key={label}
              className={`flex-1 h-1 rounded ${index <= step ? 'bg-primary-500' : 'bg-neutral-200'}`}
              title={label}
            />
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{STEPS[step]}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && isEdit ? (
              <LoadingState text="Загрузка расписания..." size="sm" inline />
            ) : (
              <>
                {step === 0 && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Название</label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Регулярная сегментная рассылка ЛК" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Тип сегмента</label>
                      <Select
                        value={segmentType}
                        onChange={(e) => {
                          setSegmentType(e.target.value as 'regular' | 'dynamic' | 'lk');
                          setSegmentId(0);
                          setSegmentName('');
                        }}
                        options={[
                          { value: 'regular', label: 'Обычный сегмент' },
                          { value: 'dynamic', label: 'Динамический сегмент' },
                          { value: 'lk', label: 'ЛК сегмент' },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Сегмент</label>
                      {tenantId ? (
                        <SegmentPicker
                          segmentType={segmentType}
                          segmentId={segmentId}
                          segmentName={segmentName}
                          tenantId={tenantId}
                          onSelect={(segmentValue, selectedName) => {
                            setSegmentId(segmentValue);
                            setSegmentName(selectedName);
                          }}
                        />
                      ) : (
                        <div className="text-sm text-neutral-500 py-2">Сначала выберите клинику.</div>
                      )}
                    </div>
                  </>
                )}

                {step === 1 && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Канал отправки</label>
                      <Select
                        value={channelOverride}
                        onChange={(e) => setChannelOverride(e.target.value)}
                        options={BROADCAST_CHANNELS}
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Если оставить авто, канал будет выбран по `patient preferred_channel`, затем по приоритету клиники.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Шаблон</label>
                      <ManualTemplatePicker templates={filteredTemplates} value={templateId} onChange={setTemplateId} />
                      {selectedTemplate && (
                        <div className="mt-2 flex justify-end">
                          <Button variant="secondary" size="sm" onClick={handleOpenTemplatePreview} disabled={templatePreviewLoading}>
                            <Eye className="w-4 h-4 mr-2" />
                            {templatePreviewLoading ? 'Строим предпросмотр…' : 'Посмотреть предпросмотр'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Тип расписания</label>
                      <Select
                        value={scheduleType}
                        onChange={(e) => setScheduleType(e.target.value as 'daily' | 'weekly' | 'cron')}
                        options={[
                          { value: 'daily', label: 'Каждый день' },
                          { value: 'weekly', label: 'Еженедельно' },
                          { value: 'cron', label: 'Cron' },
                        ]}
                      />
                    </div>
                    {scheduleType === 'cron' ? (
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Cron выражение</label>
                        <Input value={cronExpr} onChange={(e) => setCronExpr(e.target.value)} placeholder="0 19 * * *" />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Время отправки</label>
                        <Input value={sendTime} onChange={(e) => setSendTime(e.target.value)} placeholder="19:00" />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Timezone</label>
                      <Input value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="Europe/Moscow" />
                    </div>
                    <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Date guard для сегмента</label>
                        <Select
                          value={dateGuardStrategy}
                          onChange={(e) => setDateGuardStrategy(e.target.value as '' | 'same_day')}
                          options={DATE_GUARD_OPTIONS}
                        />
                        <p className="text-xs text-neutral-500 mt-1">
                          Нужен для кейсов вроде `кто был сегодня`: если сообщение дошло до auto-send или approve завтра, constraint заблокирует отправку.
                        </p>
                      </div>
                      {dateGuardStrategy && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Поля даты из сегмента</label>
                          <Input
                            value={dateGuardFields}
                            onChange={(e) => setDateGuardFields(e.target.value)}
                            placeholder="appointment_date, visit_date, APPOINTMENT_DATE"
                          />
                          <p className="text-xs text-neutral-500 mt-1">
                            Можно указать несколько полей через запятую. Guard ищет первое подходящее значение и сравнивает его с текущей датой в timezone расписания.
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Stagger, минут</label>
                        <Input value={String(staggerMinutes)} onChange={(e) => setStaggerMinutes(Number(e.target.value) || 0)} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">TTL, минут</label>
                        <Input value={String(ttlMinutes)} onChange={(e) => setTTLMinutes(Number(e.target.value) || 1440)} />
                      </div>
                    </div>
                    <div>
                      <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                        Активно сразу после сохранения
                      </label>
                    </div>
                  </>
                )}

                {step === 3 && (
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <span className="text-neutral-500">Название:</span>
                      <span className="font-medium">{name}</span>
                      <span className="text-neutral-500">Сегмент:</span>
                      <span className="font-medium">{segmentName} ({segmentType})</span>
                      <span className="text-neutral-500">Шаблон:</span>
                      <span className="font-medium">{selectedTemplate?.name || templateId}</span>
                      <span className="text-neutral-500">Канал:</span>
                      <span className="font-medium">{channelOverride || 'Авто'}</span>
                      <span className="text-neutral-500">Расписание:</span>
                      <span className="font-medium">{scheduleType === 'cron' ? cronExpr : `${scheduleType} в ${sendTime}`} / {timezone}</span>
                      <span className="text-neutral-500">Policy group:</span>
                      <span className="font-medium">segment_schedule</span>
                      <span className="text-neutral-500">Date guard:</span>
                      <span className="font-medium">{dateGuardStrategy ? `${dateGuardStrategy} / ${normalizedDateGuardFields.join(', ')}` : 'Нет'}</span>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                      Эта регулярная рассылка материализует `scheduled_messages`, а downstream сам решит: отправлять автоматически или класть в `broadcast_queue` по tenant policy для группы `segment_schedule`.
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="secondary" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0 || loading}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Назад
              </Button>
              {step < STEPS.length - 1 ? (
                <Button variant="primary" onClick={() => setStep(step + 1)} disabled={!canNext() || loading}>
                  Далее
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button variant="primary" onClick={() => void handleSave()} disabled={loading}>
                  {loading ? 'Сохранение…' : (isEdit ? 'Сохранить' : 'Создать расписание')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Modal
        isOpen={templatePreviewOpen}
        onClose={() => setTemplatePreviewOpen(false)}
        title="Предпросмотр шаблона"
        size="lg"
      >
        {templatePreviewLoading ? (
          <LoadingState text="Загрузка предпросмотра..." size="sm" inline />
        ) : templatePreviewData && selectedTemplate ? (
          <ResolvedTemplatePreview
            preview={templatePreviewData.preview}
            wabaPreview={templatePreviewData.waba_preview}
            buttons={templatePreviewData.buttons}
            variablesMissing={templatePreviewData.variables_missing}
            channel={channelOverride || selectedTemplate.messenger || undefined}
            isWabaTemplate={isWabaBackedTemplate(selectedTemplate)}
          />
        ) : null}
      </Modal>
    </MainLayout>
  );
}
